import { computed, Injectable, signal } from '@angular/core';

import type { AuthStatus, AuthedUser } from './models';

/**
 * In-memory session state. Access token never touches localStorage —
 * the refresh cookie (HTTP-only, set by the backend) is the durable
 * half of the pair, per EBT/9 §"Authentication & sessions".
 *
 * On hard reload we start in `loading` and let the interceptor's
 * boot-time refresh probe flip us to authenticated/unauthenticated.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly _accessToken = signal<string | null>(null);
  private readonly _user = signal<AuthedUser | null>(null);
  private readonly _status = signal<AuthStatus>('loading');

  readonly accessToken = this._accessToken.asReadonly();
  readonly user = this._user.asReadonly();
  readonly status = this._status.asReadonly();
  readonly isAuthenticated = computed(() => this._status() === 'authenticated');

  setSession(accessToken: string, user: AuthedUser): void {
    this._accessToken.set(accessToken);
    this._user.set(user);
    this._status.set('authenticated');
  }

  /** Update the access token without touching the user (refresh path). */
  setAccessToken(accessToken: string): void {
    this._accessToken.set(accessToken);
  }

  setUser(user: AuthedUser): void {
    this._user.set(user);
    this._status.set('authenticated');
  }

  setUnauthenticated(): void {
    this._accessToken.set(null);
    this._user.set(null);
    this._status.set('unauthenticated');
  }

  hasRole(...roleKeys: string[]): boolean {
    const u = this._user();
    return !!u && roleKeys.includes(u.roleKey);
  }

  hasPermission(...permissions: string[]): boolean {
    const u = this._user();
    if (!u) return false;
    const granted = new Set(u.permissions);
    return permissions.every((p) => granted.has(p));
  }
}
